<template>
  <div
    id="app"
    :class="[skinClasses]"
    class="h-100"
  >
    <component :is="layout" :key="layoutKey">
      <router-view />
    </component>

    <!-- Post-login loading overlay -->
    <transition name="fade">
      <div v-if="showAuthOverlay" class="auth-loading-overlay">
        <div class="auth-loading-content">
          <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;" />
          <p class="mt-1 mb-0 text-muted">Menyiapkan data...</p>
        </div>
      </div>
    </transition>

    <!-- Superadmin Banner (Bottom) -->
    <superadmin-banner 
      v-if="isSuperadminMode"
      @show-tenant-switcher="$refs.tenantSwitcher.show()"
      @show-audit-log="showAuditLog"
    />
    
    <!-- Tenant Switcher Modal -->
    <superadmin-tenant-switcher
      v-if="isSuperadminMode"
      ref="tenantSwitcher"
    />

    <!-- Audit Log Modal -->
    <b-modal
      v-if="isSuperadminMode"
      id="superadmin-audit-log-modal"
      title="Superadmin Audit Log"
      size="xl"
      scrollable
      ok-only
      ok-title="Close"
    >
      <div v-if="auditLogs && auditLogs.length">
        <b-table
          :items="auditLogs"
          :fields="[
            { key: 'created_at', label: 'Time', formatter: v => v ? new Date(v).toLocaleString('id-ID') : '-' },
            { key: 'action', label: 'Action' },
            { key: 'description', label: 'Description' },
            { key: 'ip_address', label: 'IP' },
          ]"
          small
          striped
          hover
          responsive
        />
      </div>
      <div v-else class="text-center text-muted py-2">
        No audit logs found
      </div>
    </b-modal>

    <!-- Subscription Warning/Grace Banner -->
    <subscription-banner v-if="isAuthenticated" />

    <!-- Storage Quota Banners -->
    <storage-warning-banner v-if="isAuthenticated" />
    <storage-overage-banner v-if="isAuthenticated" />

    <!-- Suspended Tenant Overlay -->
    <suspended-tenant-overlay v-if="isAuthenticated" />

    <!-- AI Consent Modal (global, Pinia-driven) -->
    <ai-consent-modal v-if="isAuthenticated" />

    <!-- Speech-to-Text Input Modal (global, Pinia-driven) -->
    <speech-input-modal v-if="isAuthenticated" />

    <!-- Only render these modals when user is authenticated to prevent unnecessary API calls on login page -->
    <template v-if="isAuthenticated">
      <modal-pasien-create
        ref="modalCreate"
        auto-navigate
        fullscreen
      />
      <modal-import-pasien />
      <print-bukti-pendaftaran modal-id="buktiPendaftaranPagePasien" />
      <modal-tambah-kunjungan
        fullscreen
        modal-id="tambahKunjunganGlobal"
        @finish="cetakBuktiPendaftaran"
      />
    </template>

    <scroll-to-top v-if="enableScrollToTop" />

    <!-- Portal target for help panel sidebar (must be outside navbar stacking context) -->
    <portal-target name="help-panel-portal" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import {$themeBreakpoints, $themeColors, $themeConfig} from '@themeConfig'
import {watch} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import {useCssVar, useWindowSize} from '@vueuse/core'

import Vue from 'vue'
import store from '@/store'

import sLocalStorage from '@core/utils/secure-storage'

import HasOperation from '@core/mixins/has-operation'
import ModalPasienCreate from '@/components/pasien/ModalPasienCreate.vue'
import ModalImportPasien from '@/components/pasien/ModalImportPasien.vue'
import PrintBuktiPendaftaran from '@/views/apps/pasien/cetakan/PrintBuktiPendaftaran.vue'
import ModalTambahKunjungan from '@/views/apps/components/ModalTambahKunjungan.vue'
import SuperadminBanner from '@/components/SuperadminBanner.vue'
import SuperadminTenantSwitcher from '@/components/SuperadminTenantSwitcher.vue'
import SubscriptionBanner from '@/components/SubscriptionBanner.vue'
import StorageWarningBanner from '@/components/StorageWarningBanner.vue'
import StorageOverageBanner from '@/components/StorageOverageBanner.vue'
import SuspendedTenantOverlay from '@/components/subscription/SuspendedTenantOverlay.vue'
import AiConsentModal from '@/components/common/AiConsentModal.vue'
import SpeechInputModal from '@/components/speech/SpeechInputModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapGetters } from 'vuex'
import jwtDefaultConfig from '@/@core/auth/jwt/jwtDefaultConfig'
// import canToast from '@core/mixins/ui/toast' // Replaced by DekaToast plugin
import { mapActions, mapState } from 'pinia'
import { useSettingStore } from './pinia/setting'
import { useAuthStore } from '@/pinia/auth'
import { useSubscriptionStore } from '@/pinia/subscription'
import { useTokenBalanceStore } from '@/pinia/tokenBalance'
import money from './@core/mixins/ui/money'

// Permissions that require a specific subscription feature to be active.
// Used to gate nav items and navbar action buttons for users without the feature.
const PERMISSION_FEATURE_GATE = {
  antrean: 'rawat_jalan',
  kunjungan: 'rawat_jalan',
  'tabel pasien': 'rawat_jalan',
  'detail pasien': 'rawat_jalan',
  pos: 'pos',
  stock: 'inventory',
  penyesuaian_stok: 'inventory',
  'buku kas': 'inventory',
  'pelunasan hutang': 'inventory',
  pengklaiman: 'pengklaiman',
  'reward programs': 'loyalty',
  'reward points': 'loyalty',
}

sLocalStorage.setSecuredFields([
  {
    name: 'userData',
    type: 'object'
  }
])

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
// const LayoutHorizontal = () =>
// import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    // LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,

    ModalPasienCreate,
    ModalImportPasien,
    ModalTambahKunjungan,
    PrintBuktiPendaftaran,
    SuperadminBanner,
    SuperadminTenantSwitcher,
    SubscriptionBanner,
    StorageWarningBanner,
    StorageOverageBanner,
    SuspendedTenantOverlay,
    AiConsentModal,
    SpeechInputModal
  },
  mixins: [HasOperation, money], // canToast removed - using DekaToast plugin
  data() {
    return {
      entityProxy: Vue.observable({ logo: '', name: '', address: '', phone: '' })
    }
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  provide() {
    return {
      // toast and bpjsToast now handled by DekaToast plugin
      hasPermission: this.hasPermission,
      entity: this.entityProxy,
      settingVal: this.settingVal,
      bvModal: this.$bvModal,
      formatMoney: this.formatMoney,
      swal: this.$swal,
      route: this.$route,
      router: this.$router
    }
  },
  computed: {
    ...mapState(useAuthStore, ['entity', 'authReady', 'token']),
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    // Key that changes when superadmin switches tenant, forcing layout re-render
    layoutKey() {
      const tenantId = this.$store.getters['superadmin/currentTenantId']
      return `layout-tenant-${tenantId || 0}`
    },
    ...mapGetters('pasien', ['allData']),

    // Superadmin computed properties
    auditLogs() {
      return this.$store.state.superadmin.auditLogs || []
    },
    isSuperadminMode() {
      const isActive = this.$store.getters['superadmin/isSuperadminMode']
      return isActive
    },

    // Use Pinia token (reactive) instead of localStorage (not reactive)
    // so changes from signOut() immediately dismiss overlays and hide auth-only components
    isAuthenticated() {
      return !!this.token
    },
    showAuthOverlay() {
      return this.isAuthenticated && !this.authReady
    }
  },
  watch: {
    entity: {
      handler(newVal) {
        if (newVal) {
          Object.keys(newVal).forEach(key => {
            Vue.set(this.entityProxy, key, newVal[key])
          })
        }
      },
      immediate: true,
      deep: true
    }
  },
  async mounted() {
    // Check if superadmin session exists in localStorage and restore it
    const superadminSession = localStorage.getItem('superadmin_session')
    if (superadminSession) {
      try {
        const session = JSON.parse(superadminSession)
        if (session.isActive) {
          // Restore superadmin mode from localStorage
          this.$store.commit('superadmin/SET_SUPERADMIN_MODE', {
            active: true,
            sessionStartTime: session.sessionStartTime,
            availableTenants: [],
            requires2FASetup: false,
            requires2FA: false
          })

          if (session.currentTenantId) {
            this.$store.commit('superadmin/SET_CURRENT_TENANT', {
              id: session.currentTenantId,
              name: session.currentTenantName || 'Unknown Tenant'
            })
          }

          // Start session timer to auto-logout when expired
          this.$store.dispatch('superadmin/startSessionTimer')
        }
      } catch (error) {
      }
    }

    // Only fetch settings and pasien data if user is logged in
    const token = localStorage.getItem(jwtDefaultConfig.storageTokenKeyName)
    if (token) {
      this.fetchSetting()
    }

    // Safety fallback: ensure overlay never blocks forever
    if (this.isAuthenticated && !this.authReady) {
      setTimeout(() => {
        if (!this.authReady) {
          this.setAuthReady(true)
        }
      }, 5000)
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'light',
      'dark'
    ]

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      )
    }

    // Set RTL
    const {isRTL} = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    ;(async () => {
      const t = localStorage.getItem(jwtDefaultConfig.storageTokenKeyName)
      if (!t) return
      try {
        const r = await this.$axios.get('/v1/user-data')
        const { userData } = r.data
        sLocalStorage.setItem('userData', JSON.stringify(userData))
        this.setPermissions([...(userData.ability.map(({action:subject}) => ({action: 'manage', subject}))), { subject: 'view dashboard' }])
        // this.$ability.update([...userData.ability, { action: 'view dashboard' }])
        this.$store.dispatch('user/setData', userData)
        this.setAuthReady(true)
        // Load subscription features immediately after auth so nav gating is ready
        useSubscriptionStore().loadSubscription()
        // Fetch AI credit balance for sidebar display
        useTokenBalanceStore().fetchBalance()
        await this.$store.dispatch('pasien/getReferences')
        this.$store.dispatch('masterdata/getSHDK')
        this.$store.dispatch('masterdata/getJK')
        this.$store.dispatch('masterdata/getPendidikan')
        this.$store.dispatch('masterdata/getPekerjaan')
        this.$store.dispatch('masterdata/getJenisKontak')
        this.$store.dispatch('masterdata/getProvinsi')
        this.$store.dispatch('masterdata/getProfesi')
        this.$store.dispatch('masterdata/getAgama')
        this.$store.dispatch('masterdata/getSmf')
      } catch (e) {
        if (e.response && e.response.status === 401) {
          this.$store.dispatch('auth/logout')
        }
      }
    })()
    // this.$store.dispatch('masterdata/getProdukObat')
    // setInterval(() => {
    //   this.$store.dispatch('masterdata/getProdukObat')
    // }, 3000 * 60) // 1 menit
  },
  methods: {
    ...mapActions(useSettingStore, { fetchSetting: 'fetchSetting', settingVal: 'val' }),
    ...mapActions(useAuthStore, {
      ableTo: 'ableTo',
      setPermissions: 'setPermissions',
      setAuthReady: 'setAuthReady',
    }),
    hasPermission(action, perms) {
      if (!perms) return false
      if (!Array.isArray(perms)) perms = [perms]

      // Superadmin bypasses all feature gating
      const authStore = useAuthStore()
      const roles = (authStore.userData && authStore.userData.roles) || []
      const isSuperadmin = roles.includes('superadmin')

      return !!perms.filter((perm) => {
        if (!this.ableTo(action, perm)) return false
        if (isSuperadmin) return true
        // Check if this permission requires a subscription feature
        const requiredFeature = PERMISSION_FEATURE_GATE[perm]
        if (!requiredFeature) return true
        const subscriptionStore = useSubscriptionStore()
        // If subscription not loaded yet, allow through (avoid blocking on boot)
        if (!subscriptionStore.currentSubscription) return true
        return subscriptionStore.activeFeatures.includes(requiredFeature)
      }).length
    },
    runGlobalEvent() {
      if (this.hasGlobalEvent('createPasien')) {
        this.openModalCreate()
        this.$store.dispatch('app/removeOperation', ['createPasien'])
      }
      if (this.hasGlobalEvent('createKunjungan')) {
        this.$bvModal.show('tambahKunjunganGlobal')
        this.$store.dispatch('app/removeOperation', ['createKunjungan'])
      }
    },
    openModalCreate() {
      // this.$refs.modalCreate.$refs.wizard.reset()
      // this.$refs.modalCreate.$refs.step1.reset()
      this.$store.commit('pasien/UPDATE_FORM_EDIT', {})
      this.$bvModal.show('modal-create')
    },
    cetakBuktiPendaftaran() {
      // Konfirmasi cetak bukti pendaftaran dinonaktifkan — petugas tidak
      // menggunakannya. Bukti pendaftaran tetap bisa dicetak manual dari
      // tombol printer di daftar pendaftaran. Hapus komentar untuk mengaktifkan.
      // this.$bvModal
      //   .msgBoxConfirm('Apakah anda ingin mencetak bukti pendaftaran', {
      //     title: 'Konfirmasi Cetak Bukti Pendaftaran',
      //     size: 'sm',
      //     buttonSize: 'sm',
      //     okVariant: 'success',
      //     okTitle: 'Cetak',
      //     cancelTitle: 'Tidak',
      //     footerClass: 'p-2',
      //     hideHeaderClose: false,
      //     centered: true
      //   })
      //   .then(async (v) => {
      //     if (!v) return
      //     await this.$printDirect('buktiPendaftaranPagePasien', {
      //       styles: ['/print/style.css', '/print/buktiPendaftaran.css']
      //     })
      //   })
      //   .catch((_err) => {
      //     // An error occurred
      //   })
    },
    
    // Superadmin methods
    showAuditLog() {
      this.$bvModal.show('superadmin-audit-log-modal')
    }
  },
  setup() {
    const {skin, skinClasses} = useAppConfig()
    const {enableScrollToTop} = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Note: provideToast removed - DekaToast plugin handles all toast functionality
    // The provideToast call was causing Vue warnings about provide() being called
    // outside of setup() context in Vue 2 Composition API

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const {width: windowWidth} = useWindowSize()
    watch(windowWidth, (val) => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop
    }
  }
}
</script>

<style scoped>
.auth-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.auth-loading-content {
  text-align: center;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
<style>
.dark-layout .auth-loading-overlay {
  background: rgba(22, 29, 49, 0.95);
}

/* ── Aksita chatbot launcher overrides ──────────────────────────
   Brand-color the launcher (instead of default black) and shrink
   it slightly to match the previous Feedback button footprint.
   Aksita applies its styles inline, so !important is required. */
button#aksita-chat-button {
  background: linear-gradient(135deg, #1c0baf, #06022f) !important;
  width: 48px !important;
  height: 48px !important;
  box-shadow: 0 4px 14px rgba(28, 11, 175, 0.4) !important;
  transform: none !important;
}

button#aksita-chat-button:hover {
  box-shadow: 0 6px 20px rgba(28, 11, 175, 0.55) !important;
  transform: translateY(-2px) !important;
}

button#aksita-chat-button img {
  width: 28px !important;
  height: 28px !important;
}
</style>
